import { type EdgeData, edgeIdFor, type NodeData, toVisualGraph } from "#puf/machines/flow.ts";
import { STARTER_LAYOUT, STARTER_MACHINE } from "#puf/machines/starter.ts";
import { store } from "#puf/store/index.ts";
import { useSelector } from "@xstate/store-react";
import { Background, Controls, MiniMap, ReactFlow } from "@xyflow/react";
import type { Connection, Edge, EdgeMouseHandler, Node, NodeMouseHandler, NodeTypes, OnNodeDrag } from "@xyflow/react";
import { useMemo } from "react";
import { StateNode } from "./StateNode.tsx";

const nodeTypes: NodeTypes = { state: StateNode };

/**
 * The xyflow canvas. Reads stateMachine + machineLayout + selection from the
 * store, computes nodes/edges via `flow.ts` (which round-trips through
 * @statelyai/graph), and dispatches store events for every editor gesture.
 *
 * Falls back to the starter machine layout when stateMachine is null (fresh
 * project). The first user gesture (Add State or any state.* event) replaces
 * the fallback with a real machine.
 */
export function MachineCanvas() {
  const stateMachine = useSelector(store, (s) => s.context.stateMachine);
  const machineLayout = useSelector(store, (s) => s.context.machineLayout);
  const selectedStateId = useSelector(store, (s) => s.context.selectedStateId);
  const selectedTransitionIndex = useSelector(store, (s) => s.context.selectedTransitionIndex);

  const machine = stateMachine ?? STARTER_MACHINE;
  const layout = stateMachine ? machineLayout : STARTER_LAYOUT;

  // xyflow node/edge arrays — recomputed when machine or layout changes.
  // We bypass `@statelyai/graph`'s `toXYFlow` here because it erases the
  // generic data types; building Node/Edge from our typed VisualGraph keeps
  // NodeData / EdgeData precise end-to-end. The `@statelyai/graph` layer is
  // still used inside flow.ts for algorithms (validate.ts BFS reachability).
  const { nodes, edges } = useMemo(() => {
    const visual = toVisualGraph(machine, layout);
    const ns: Node<NodeData>[] = visual.nodes.map((n) => ({
      id: n.id,
      type: "state",
      position: { x: n.x, y: n.y },
      data: n.data,
      selected: n.id === selectedStateId,
    }));
    const es: Edge<EdgeData>[] = visual.edges.map((e) => ({
      id: edgeIdFor(e.data.transitionIndex),
      source: e.sourceId,
      target: e.targetId,
      label: e.data.event,
      data: e.data,
      selected: e.data.transitionIndex === selectedTransitionIndex,
      animated: e.data.trigger === "onLoopEnd",
    }));
    return { nodes: ns, edges: es };
  }, [machine, layout, selectedStateId, selectedTransitionIndex]);

  const onNodeDragStop: OnNodeDrag = (_event, node) => {
    store.trigger["state.move"]({ id: node.id, position: { x: node.position.x, y: node.position.y } });
  };

  const onNodeClick: NodeMouseHandler = (_event, node) => {
    store.trigger["editor.selectState"]({ id: node.id });
  };

  const onEdgeClick: EdgeMouseHandler<Edge<EdgeData>> = (_event, edge) => {
    const data = edge.data;
    if (!data) return;
    store.trigger["editor.selectTransition"]({ index: data.transitionIndex });
  };

  function onConnect(connection: Connection) {
    if (!connection.source || !connection.target) return;
    store.trigger["transition.add"]({
      transition: { from: connection.source, to: connection.target, event: "EVENT", trigger: "manual" },
    });
  }

  function onPaneClick() {
    store.trigger["editor.clearSelection"]();
  }

  return (
    <div className="w-full h-full relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeDragStop={onNodeDragStop}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onConnect={onConnect}
        onPaneClick={onPaneClick}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#2a2d31" gap={16} />
        <MiniMap pannable zoomable maskColor="rgba(0,0,0,0.5)" />
        <Controls />
      </ReactFlow>
    </div>
  );
}
